import { Injectable } from "@angular/core";
import { OptionType } from 'ng/src/app/core/Option';
import { Utils } from "ng/src/app/core/utils";
import { ComputedValue } from 'ng/src/app/core/value';
import { DestroyableComponent } from "ng/src/app/shared/destroyable.component";
import { BasicRecord, JSON, Nullish, Optional } from 'ng/src/app/shared/model/app.model';
import { AcceptableTypedTreeDropEvent } from "ng/src/app/shared/model/primeng-extensions.model";
import { EditorFooterTreeNode, EditorTreeNode, TemplateEditorComponent } from 'ng/src/app/templates/editor/template-editor.component';
import { ReadonlyContainerEntry } from 'ng/src/app/templates/shared/template-container/template-container.model';
import { TemplateSegment, TemplateSegmentTypeId, TemplateTypeId, TypedTemplateSegment } from "ng/src/app/templates/shared/template.model";
import { TemplateService } from "ng/src/app/templates/shared/template.service";
import { Confirmation } from "primeng/api";
import { Observable, Subject } from "rxjs";

@Injectable()
export class TemplateEditorService extends DestroyableComponent {
	
	private readonly onConfirmSubject = new Subject<Confirmation>();
	readonly onConfirm$ = this.onConfirmSubject.asObservable();
	
	constructor(
		private templateService: TemplateService
	) {
		super();
	}
	
	get templateType() {
		return this.templateEditor.templateType;
	}
	
	get templateEditor() {
		if (!this._templateEditor) {
			throw new Error("Instance of Template Editor component has not been provided");
		}
		
		return this._templateEditor;
	}
	
	set templateEditor(val) {
		this._templateEditor = val;
	}
	
	private _templateEditor!: TemplateEditorComponent;
	
	private dropContext?: DropContext;
	
	get treeNodes() {
		return this._treeNodeValue.value;
	}
	
	private _treeNodeValue = new ComputedValue({
		reference: () => this.templateEditor.templateContainer,
		get: (container) => {
			if (container == null) {
				return [] as EditorTreeNode[];
			}
			
			if (this.templateEditor.showObsolete) {
				return this.createTreeNodes([...container.entries]);
			}
			
			return this.createTreeNodes([...container.getNonObsoleteView().entries]);
		}
	});
	
	onDrop(event: DropEvent) {
		console.log(event);
		
		const context = this.dropContext = this.createContext(event);
		console.log('Drop Level', context.dropLevel);
		
		let dropIndex = event.index - 1;
		
		if (!context.dragSegment)
			return;
		
		// Disallow dropping standard entry segments onto standard entry segments.
		if (context.dropSegment && context.dropSegment.type.id !== TemplateSegmentTypeId.SUB_TEMPLATE && context.dropIsOnSegment)
			return;
		
		if (context.dragSegment?.type.id !== TemplateSegmentTypeId.SUB_TEMPLATE) {
			// Use the event index since drop points are indexed by their parent.
			dropIndex = event.index;
			
			// Add 1 for the offset if the drop is on the last drop point due to needing to be indexed lower than the actual index.
			if (context.isLastDroppoint) {
				dropIndex += 1;
			}
			
			const executeAction = () => {
				this.moveIntoParent([context.dragEntry!], context.parentEntry!, context.dropIsOnSegment ? undefined : dropIndex);
			};
			
			if (context.dragEntry?.parent !== context.parentEntry) {
				this.confirm(`Move ${this.templateEditor.getName(context.dragSegment)} into ${context.isOperational ? 'sub template' : 'section'} ${this.templateEditor.getName(context.parentEntry?.segment!)}?`,
					executeAction);
				return;
			}
			else if (context.parentEntry) {
				executeAction();
			}
			else if (context.dropSegment) {
				const toMove = [context.dragSegment];
				const parentEntries = OptionType.nonNull(context.dropEntry?.parent?.entries)
					.orThrow(new Error('Cannot find parent entries'))
					.filter((e) => e.length > 0)
					.getOrThrow(new Error('Parent entries is empty'));
				
				const after = parentEntries[dropIndex]?.segment ?? 'TOP';
				this.templateService.moveSegments(toMove, after)
					.subscribe(() => this.templateEditor.refresh());
			}
		}
		else if (context.dragSegment.type.id === TemplateSegmentTypeId.SUB_TEMPLATE) {
			if (context.dropIsOnSegment && context.parentEntry) {
				dropIndex = context.parentEntry.entries.length - 1;
			}
			
			if (!context.dropSegment) {
				this.confirm(`Would you like to merge the intro section into "${this.templateEditor.getName(context.dragSegment)}"?
				After doing so, all current intro segments will appear before the current segments of "${this.templateEditor.getName(context.dragSegment)}".`, () => {
					this.mergeIntroInto(this.dropContext?.dropEntry!, this.dropContext?.dragEntry!);
				})
			}
			else if (context.dropLevel === 1 && !context.dropIsOnSegment) {
				//If the drop is on first index, we know it wants to be placed at the top.
				if (context.dropIsFirstIndex) {
					dropIndex = -1;
				}
				// If the drop is on the last index, we increment by one due to index being offset by one.
				else if (context.isLastDroppoint) {
					dropIndex += 1;
				}
				
				this.moveSectionUpToLevelOne(context.dragEntry!, dropIndex);
			}
			else {
				const executeAction = () => {
					this.moveSectionIntoSection(this.dropContext?.dragEntry!, this.dropContext?.parentEntry!, dropIndex);
				}
				
				if (context.dragEntry?.parent !== context.parentEntry) {
					this.confirm(`Move ${this.templateEditor.getName(context.dragSegment)} and all segments into section ${this.templateEditor.getName(context.parentEntry?.segment!)}?`, executeAction);
				}
				else {
					executeAction();
				}
			}
		}
		
	}
	
	moveSectionUpToLevelOne(section: ReadonlyContainerEntry, index: number) {
		const sectionSegment = section.segment;
		if (!sectionSegment)
			throw new Error('Cannot process sectional move on undefined segments')
		if (!TemplateSegment.isType(sectionSegment, TemplateSegmentTypeId.SUB_TEMPLATE))
			throw new Error('Cannot process sectional move on non sectional segments');
		
		let afterNode: Optional<ReadonlyContainerEntry>;
		
		if (index >= 0) {
			const afterSection = section.container.entries[index];
			if (afterSection == null) {
				throw new Error('Cannot find after section');
			}
			
			const children = this.flattenContainerEntry(afterSection);
			const filteredForSelf = children.filter(n => n != section);
			afterNode = filteredForSelf[filteredForSelf.length - 1];
		}
		
		this.patchSectionToLevel(section, { afterNode, toLevel: 1 });
	}
	
	moveSectionIntoSection(section: ReadonlyContainerEntry, intoSection: ReadonlyContainerEntry, fromIdx?: number) {
		const sectionSegment = section.segment;
		const intoSegment = intoSection.segment;
		
		if (!sectionSegment || !intoSegment)
			throw new Error('Cannot process sectional move on undefined segments');
		if (sectionSegment.type.id !== TemplateSegmentTypeId.SUB_TEMPLATE || intoSegment.type.id !== TemplateSegmentTypeId.SUB_TEMPLATE)
			throw new Error('Cannot process sectional move on non sectional segments');
		
		let children = intoSection.entries;
		let afterNode: Optional<ReadonlyContainerEntry>;
		
		if (!Utils.isValidNumber(fromIdx)) {
			afterNode = this.lastNodeWhere(children, node => !TemplateSegment.isType(node.segment, TemplateSegmentTypeId.SUB_TEMPLATE))
		}
		else {
			let cValue: ReadonlyContainerEntry;
			
			// If the index is -1, then the section is being moved to the top and needs to be inserted after the section segment
			if (fromIdx === -1) {
				cValue = intoSection;
				afterNode = cValue;
			}
			else {
				afterNode = this.findAnchorSegmentForSection(intoSection, 'section', fromIdx);
			}
		}
		
		if (!afterNode) afterNode = intoSection;
		this.patchSectionToLevel(section, { afterNode, toLevel: intoSegment.level! + 1 });
	}
	
	patchSectionToLevel(section: ReadonlyContainerEntry, options: { afterNode?: ReadonlyContainerEntry, toLevel?: number }) {
		let { afterNode, toLevel } = options;
		
		const sectionSegment = section.segment;
		const afterSegment = afterNode?.segment;
		
		if (!Utils.isValidNumber(toLevel) && TemplateSegment.isType(afterSegment!, TemplateSegmentTypeId.SUB_TEMPLATE))
			toLevel = afterSegment.level! + 1;
		
		if (!sectionSegment)
			throw new Error('Section segment is undefined in tree node');
		if (!Utils.isValidNumber(toLevel) || !Utils.isInRange(toLevel, 0, 5)) {
			throw new Error(`Section target level is invalid: ${toLevel}: Required (1-5)`);
		}
		
		const newLevelMap: Record<number, number> = {
			[sectionSegment.id]: Math.min(toLevel, 5)
		};
		
		const treeSegmentMap: Record<number, ReadonlyContainerEntry> = {};
		const segments = this.flattenContainerEntry(section).map(n => {
			const data = n.segment!;
			treeSegmentMap[data.id] = n;
			return data;
		});
		
		this.templateService.moveSegments(segments, afterSegment ?? 'TOP', sectionSegment => {
			if (this.templateType === TemplateTypeId.OPERATIONAL)
				return;
			
			return findNewSectionLevel(sectionSegment);
			
			function findNewSectionLevel(tSegment: TypedTemplateSegment<TemplateSegmentTypeId.SUB_TEMPLATE>): number {
				if (tSegment.id in newLevelMap)
					return newLevelMap[tSegment.id];
				
				const parent = treeSegmentMap[tSegment.id].parent!;
				const parentLevel = findNewSectionLevel(parent.segment! as TypedTemplateSegment<TemplateSegmentTypeId.SUB_TEMPLATE>);
				return newLevelMap[tSegment.id] = Math.min( parentLevel + 1, 5);
			}
		})
			.subscribe(() => this.templateEditor.refresh());
	}
	
	/**
	 * Moves the specified nodes into the given parent node at an optional position.
	 *
	 * @param {ReadonlyContainerEntry[]} moveNodes - The nodes to be moved. These must not include section-type nodes.
	 * @param {ReadonlyContainerEntry} parent - The target parent node where the nodes will be moved. The parent must be a section node.
	 * @param {number} [insertAt] - An optional index determining the position within the parent to insert the nodes.
	 * @return A subscription object that completes once the nodes are successfully moved and the template editor is refreshed.
	 * @throws {Error} If the parent node is not a section node, or if the moveNodes array contains any section-type nodes.
	 */
	moveIntoParent(moveNodes: ReadonlyContainerEntry[], parent: ReadonlyContainerEntry, insertAt?: number) {
		if (parent.segment && !TemplateSegment.isType(parent.segment, TemplateSegmentTypeId.SUB_TEMPLATE)) {
			throw new Error('Parent must be a section node');
		}
		
		const hasAnySections = moveNodes.some(n => TemplateSegment.isType(n.segment, TemplateSegmentTypeId.SUB_TEMPLATE));
		
		if (hasAnySections) {
			throw new Error('Cannot move sections with this method');
		}
		
		const afterEntry = this.findAnchorSegmentForSection(parent, 'segment', insertAt);
		const moveSegments = moveNodes.map(n => n.segment!);
		
		return this.templateService.moveSegments(moveSegments, afterEntry?.segment ?? 'TOP')
			.subscribe(() => this.templateEditor.refresh());
	}
	
	/**
	 * Copies the provided nodes into the specified parent container at an optional index.
	 *
	 * @param {ReadonlyContainerEntry[]} copyNodes - An array of nodes to be copied into the parent container.
	 * @param {ReadonlyContainerEntry} parent - The parent container into which the nodes will be copied. Must be a section node.
	 * @param {number} [insertAt] - Optional index specifying the position in the parent container where the nodes should be inserted. If omitted, the nodes are appended at the default position.
	 * @return An observable that subscribes to the result of the copy operation, providing feedback on its execution state or outcome.
	 * @throws {Error} - Throws an error if the parent is not a valid section node or if unable to determine the insertion anchor point.
	 */
	copyIntoParent(copyNodes: ReadonlyContainerEntry[], parent: ReadonlyContainerEntry, insertAt?: number) {
		if (parent.segment && !TemplateSegment.isType(parent.segment, TemplateSegmentTypeId.SUB_TEMPLATE)) {
			throw new Error('Parent must be a section node');
		}
		
		const hasAnySections = copyNodes.some(n => TemplateSegment.isType(n.segment, TemplateSegmentTypeId.SUB_TEMPLATE));
		const afterEntry = this.findAnchorSegmentForSection(parent, hasAnySections ? 'section' : 'segment', insertAt);
		const copySegments = copyNodes.map(n => n.segment!);
		
		if (!afterEntry) {
			throw new Error('Cannot find entry to insert after');
		}
		
		let newLevelMap: BasicRecord<number> | null = null;
		if (parent.container.templateTypeId === TemplateTypeId.INSPECTION) {
			newLevelMap = this.createUpdatedSectionMap(parent, copyNodes);
		}
		
		return this.templateService.copySegments(copySegments, (ts, idx) => {
			const transform: JSON.Object = {
				newTemplate: afterEntry.container.templateId
			}
			
			if (idx === 0) {
				if (afterEntry.segment) {
					transform.prevItemId = afterEntry.segment.id;
				}
				else {
					transform.sortOrder = -1;
				}
			}
			else {
				transform.prevItemId = 'completed[currentIndex - 1].id';
			}
			
			if (!TemplateSegment.isType(ts, TemplateSegmentTypeId.SUB_TEMPLATE) || newLevelMap == null) {
				return transform;
			}
			
			transform.level = newLevelMap[ts.id];
			
			return transform;
		})
			.subscribe();
	}
	
	mergeIntroInto(introNode: ReadonlyContainerEntry, destinationNode: ReadonlyContainerEntry) {
		if (introNode.segment) {
			throw new Error('Intro node cannot have a template segment associated');
		}
		if (!TemplateSegment.isType(destinationNode.segment, TemplateSegmentTypeId.SUB_TEMPLATE)) {
			throw new Error('Destination of Intro merge must be a section');
		}
		
		const afterSegment = destinationNode.segment;
		const segments = this.flattenContainerEntry(introNode)
			.filter(n => !!n.segment)
			.map(n => n.segment!);
		
		return this.templateService.moveSegments(segments, afterSegment).subscribe(() => this.templateEditor.refresh());
	}
	
	findDropLevel(element: HTMLElement): number {
		let count = 0;
		let current = Nullish.of(element)
		while (current) {
			if (current.nodeName === 'P-TREE') {
				break;
			}
			else if (current.nodeName === 'P-TREENODE') {
				count += 1;
			}
			current = current.parentElement;
		}
		return count;
	}
	
	dropPointIsLastOfList(element: HTMLElement, relatedEntry: ReadonlyContainerEntry) {
		if (!element.classList.contains('p-treenode-droppoint') || !element.parentElement) {
			return false;
		}
		
		const parentChildren = relatedEntry.parent?.entries ?? [];
		if (parentChildren.length > 0 && parentChildren.indexOf(relatedEntry) < parentChildren.length - 1) {
			return false;
		}
		
		return element.parentElement.lastElementChild === element;
	}
	
	lastNodeWhere(nodes: readonly ReadonlyContainerEntry[], predicate: (n: ReadonlyContainerEntry) => boolean) {
		const copy = Array.from(nodes);
		copy.reverse();
		
		return copy.find(predicate);
	}
	
	createNewSectionRelativeToSection(parent: ReadonlyContainerEntry, relative: 'into' | 'after', options: Record<string, any>) {
		if (parent.segment && !TemplateSegment.isType(parent.segment, TemplateSegmentTypeId.SUB_TEMPLATE)) {
			throw new Error(`Cannot create sub template under parent type ${parent.segment?.type?.id}`);
		}
		
		if (this.templateType === TemplateTypeId.OPERATIONAL && relative === 'into') {
			throw new Error('Cannot put sub template into another sub template');
		}
		
		const flattenedNodes = this.flattenContainerEntry(parent);
		const afterNode = flattenedNodes[flattenedNodes.length - 1];
		const afterSegment = afterNode.segment;
		
		const level = Utils.guardLet(parent.segment?.level, parentLevel => Math.min(parentLevel + (relative === 'into' ? 1 : 0), 5)) ?? 1;
		
		return this.createSection({
			...options,
			...afterSegment ? {
				prevItemId: afterSegment.id,
			} : {},
			...this.templateType === TemplateTypeId.INSPECTION && {
				level
			}
		})
	}
	
	// Creates a new section segment with the options provided and auto inserts template and type based on the service state.
	createSection(options: BasicRecord): Observable<unknown> {
		const opts = {
			...this.templateType === TemplateTypeId.INSPECTION && {
				level: 1,
			},
			...options,
			template: `/api/templates/${this.templateEditor.template.id}`,
			type: `/api/templateTypes/${TemplateSegmentTypeId.SUB_TEMPLATE}`,
		}
		
		return this.templateService.postTemplateSegment(opts);
	}
	
	confirm(message: string, callback: () => void) {
		this.onConfirmSubject.next({
			key: this.templateEditor.confirmPopupKey,
			header: `Moving ${this.dropContext?.dragSegment?.name}`,
			message,
			accept: callback,
			acceptLabel: 'Proceed',
			rejectLabel: 'Cancel',
			acceptButtonStyleClass: 'p-button-confirm',
			rejectButtonStyleClass: 'p-button-secondary',
			defaultFocus: 'reject'
		});
	}
	
	private flattenContainerEntry(node: ReadonlyContainerEntry): ReadonlyContainerEntry[] {
		const nodes = [] as ReadonlyContainerEntry[];
		
		flatten(node);
		
		return nodes;
		
		function flatten(node: ReadonlyContainerEntry) {
			nodes.push(node);
			node.entries.forEach(n => flatten(n));
		}
	}
	
	
	/**
	 * Finds a segment that is a compatible candidate when inserting into a section.
	 *
	 * @param parent The section being inserted into
	 * @param anchorType The type of segment that will be anchored. If a segment, the anchor can be another segment.
	 * If a section, the anchor needs to be a segment at the end of other sections, or a section if the section has no segments.
	 * @param insertAt The index where the segment will be inserted. The index considers 0 or less to be the first segment.
	 */
	private findAnchorSegmentForSection(parent: ReadonlyContainerEntry, anchorType: 'section' | 'segment', insertAt?: number): ReadonlyContainerEntry | undefined {
		if (parent.segment && !TemplateSegment.isType(parent.segment, TemplateSegmentTypeId.SUB_TEMPLATE)) {
			throw new Error('Parent must be a section node');
		}
		
		const anchorEntries = [parent, ...parent.entries];
		const start = Utils.isValidNumber(insertAt) ? Utils.clamp(insertAt, 0, anchorEntries.length - 1) : 0;
		
		// Segment can anchor to section so we can insert parent
		if (anchorType === 'segment' && start <= 0) {
			return parent;
		}
		
		
		let afterEntry = Optional.of<ReadonlyContainerEntry>();
		
		for (let i = start; i < anchorEntries.length; i++) {
			// If we are looking to anchor a segment, we can break once we find a segment as the anchor.
			if (anchorType === 'segment' && afterEntry != null) {
				break;
			}
			
			const child = anchorEntries[i];
			const nextSegment = child?.segment;
			if (nextSegment == null) {
				continue;
			}
			
			// If the next segment is a section, we can break since we found a segment.
			if (afterEntry != null && TemplateSegment.isType(nextSegment, TemplateSegmentTypeId.SUB_TEMPLATE)) {
				break;
			}
			
			afterEntry = child;
			
			// If the processed segment is a section, we can break. We will anchor to the last child of the section.
			if (TemplateSegment.isType(afterEntry?.segment, TemplateSegmentTypeId.SUB_TEMPLATE)) {
				break;
			}
		}
		
		// If the anchor segment is a section, we need to get the last child of the found section to anchor to.
		if (TemplateSegment.isType(afterEntry?.segment, TemplateSegmentTypeId.SUB_TEMPLATE) && anchorType === 'section') {
			const afterSegmentFlattened = this.flattenContainerEntry(parent);
			return afterSegmentFlattened[afterSegmentFlattened.length - 1];
		}
		
		afterEntry ??= parent;
		
		return afterEntry;
	}
	
	/**
	 * Creates a map with updated levels for the segments in the given section in relation to the given section.
	 * @param intoSection Section to create the map for
	 * @param segments Segments being moved into the section
	 * @private
	 *
	 * @returns A map of segment IDs to their new levels, or null if the section/sub template does not have a level.
	 * @throws Error If the intoSection is not a sectional segment.
	 */
	private createUpdatedSectionMap(intoSection: ReadonlyContainerEntry, segments: ReadonlyContainerEntry[]): BasicRecord<number> | null {
		if (intoSection.segment && !TemplateSegment.isType(intoSection.segment, TemplateSegmentTypeId.SUB_TEMPLATE)) {
			throw new Error('Cannot create section map for non-section node');
		}
		
		const intoSectionId = intoSection.segment?.id ?? -1;
		const intoSectionLevel = intoSection?.segment?.level ?? 0;
		
		const flattened = segments.map(n => this.flattenContainerEntry(n));
		const nodeMap = Utils.arrayToMap(flattened.flat(), n => n.segment!.id);
		const levelMap: Record<number, number> = {
			[intoSectionId]: Math.min(intoSectionLevel, 5)
		};
		
		flattened.forEach(nodeArr => {
			nodeArr.forEach(node => {
				const ts = node.segment;
				if (!TemplateSegment.isType(ts, TemplateSegmentTypeId.SUB_TEMPLATE))
					return;
				
				levelMap[ts.id] = findLevel(node);
			});
		});
		
		return levelMap;
		
		function findLevel(node: ReadonlyContainerEntry): number {
			const id = node.segment!.id;
			if (node === intoSection) {
				return levelMap[id] = Math.min(intoSectionLevel + 1, 5);
			}
			else if (id in levelMap) {
				return levelMap[id];
			}
			
			const parentNode = node.parent && node.parent.segment!.id in nodeMap ? node.parent : intoSection;
			const parentLevel = findLevel(parentNode);
			return levelMap[id] = Math.min(parentLevel + 1, 5)
		}
	}
	
	private createContext(event: DropEvent): DropContext {
		const { dropNode, dragNode } = event;
		const dragSegment = event.dragNode.data;
		const dropSegment = event.dropNode.data;
		const dropIsOnSegment = !(event.originalEvent.target as HTMLElement).classList.contains('p-treenode-droppoint');
		const dropLevel = this.findDropLevel(event.originalEvent.target as HTMLElement);
		const isOperational = this.templateType === TemplateTypeId.OPERATIONAL;
		
		const dragEntry = dragNode.containerEntry;
		
		const dropEntry = OptionType.of(dropNode)
			.filter(n => n.type !== 'section-footer')
			.fork({
				some: (n) => n.containerEntry,
				none: () => {
					// If we are dropped on to a section footer, we take the last child of the parent container.
					return OptionType.nonNull(dropNode.parent)
						.map((p) => {
							return (p as EditorTreeNode).containerEntry;
						})
						.nonNullable()
						.orThrow(new Error('Cannot find parent container of section-footer'))
						.map(e => e.entries[e.entries.length - 1])
				}
			})
			.nonNullable()
			.getOrThrow(new Error('Cannot find template container for drop node'));
		
		// When dropped onto a segment instead of the drop point, the drop node will be a standard segment instead of a
		// section segment, which is needed.
		const parentEntry = !dropSegment || (dropSegment.type.id === TemplateSegmentTypeId.SUB_TEMPLATE && dropIsOnSegment)
			? dropEntry
			: dropEntry?.parent;
		
		const dropIsFirstIndex = event.index === 0;
		
		const isLastDroppoint = dropEntry ? this.dropPointIsLastOfList(event.originalEvent.target as HTMLElement, dropEntry) : false;
		
		return {
			// dropNode,
			// dragNode,
			// parentNode,
			dragEntry,
			dropEntry,
			parentEntry,
			dragSegment,
			dropSegment,
			dropIsOnSegment,
			dropLevel,
			isOperational,
			isLastDroppoint,
			dropIsFirstIndex
		}
	}
	
	private createTreeNodes(entries: ReadonlyContainerEntry[]): EditorTreeNode[] {
		const self = this;
		const first = entries[0];
		// Ignore "Intro to template" sections
		if (this.templateType === TemplateTypeId.INSPECTION && entries.length === 1 && first?.segment == null && (first?.entries?.length ?? 0) === 0) {
			return [];
		}
		
		return entries.map(entry => {
			return createNode(entry);
		})
		
		function createNode(entry: ReadonlyContainerEntry): EditorTreeNode {
			return Utils.also({
				key: (entry.segment?.id ?? -1).toString(),
				selectable: !!entry.segment,
				data: entry.segment,
				containerEntry: entry,
			} as EditorTreeNode, node => {
				const id = entry.segment?.id ?? -1;
				const segment = entry.segment;
				
				node.draggable = !!segment;
				node.children = entry.entries.map(e => Utils.also(createNode(e), n => n.parent = node));
				node.leaf = !segment || segment?.type?.id !== TemplateSegmentTypeId.SUB_TEMPLATE;
				
				if (!node.leaf && self.templateType === TemplateTypeId.INSPECTION) {
					const sectionNode: EditorFooterTreeNode = {
						parent: node,
						selectable: false,
						draggable: false,
						droppable: false,
						data: node.data,
						label: `End of ${self.templateEditor.getName(segment)}`,
						children: [],
						leaf: true,
						prevId: node.children[node.children.length - 1]?.data?.id ?? node.data?.id,
						type: 'section-footer'
					};
					
					node.children.push(sectionNode);
				}
				
				
				let defaultOpened = false;
				if (segment == null || segment.type.id === TemplateSegmentTypeId.SUB_TEMPLATE) {
					defaultOpened = (!segment || node.children.every(n => n.data?.type.id === TemplateSegmentTypeId.SUB_TEMPLATE));
				}
				
				Utils.defineGetterSetters(node, {
					expanded: {
						get: () => {
							const open = self.templateEditor.segmentOpenMap[id] ?? defaultOpened;
							return self.templateEditor.segmentOpenMap[id] = open;
						},
						set: (val: boolean) => {
							self.templateEditor.segmentOpenMap[id] = val;
						}
					}
				});
			})
		}
	}
}

type DropEvent = AcceptableTypedTreeDropEvent<EditorTreeNode>;
type DropContext = {
	// dropNode: EditorTreeNode,
	// dragNode: EditorTreeNode,
	// parentNode: EditorTreeNode | undefined,
	dragEntry: ReadonlyContainerEntry | undefined,
	dropEntry: ReadonlyContainerEntry | undefined,
	parentEntry: ReadonlyContainerEntry | undefined,
	dragSegment: EditorTreeNode['data'],
	dropSegment: EditorTreeNode['data'],
	dropIsFirstIndex: boolean,
	isLastDroppoint: boolean,
	dropIsOnSegment: boolean,
	dropLevel: number,
	isOperational: boolean
}